import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoadingState } from './LoadingState';
import { ErrorAlert } from './ErrorAlert';
import { analyticsService } from '../../services/analyticsService';
import { notificationService } from '../../services/notificationService';
import { tokens } from '../../theme/tokens';

const PAGE_SIZE = 30;

const HEADINGS = {
  likes: 'Liked by',
  shares: 'Shared by',
  comments: 'Commented by',
  views: 'Viewed by',
  opens: 'Opened by',
  clicks: 'Clicked by',
};

/**
 * Side sheet of people for a post or a sent alert.
 */
export function EngagementPeopleDrawer({
  open,
  onClose,
  articleId,
  notificationId,
  kind = 'likes',
  title,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [nextOffset, setNextOffset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const loadPage = useCallback(
    async (offset, append) => {
      if ((!articleId && !notificationId) || !open) return;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const data = notificationId
          ? await notificationService.people(notificationId, {
              kind,
              limit: PAGE_SIZE,
              offset,
            })
          : await analyticsService.articlePeople(articleId, {
              kind,
              limit: PAGE_SIZE,
              offset,
            });
        const page = data?.items ?? [];
        setItems((prev) => (append ? [...prev, ...page] : page));
        setTotal(data?.meta?.total ?? page.length);
        setNextOffset(
          data?.meta?.nextOffset === undefined || data?.meta?.nextOffset === null
            ? null
            : data.meta.nextOffset,
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [articleId, notificationId, kind, open],
  );

  useEffect(() => {
    if (!open) return;
    setItems([]);
    setNextOffset(null);
    setTotal(0);
    void loadPage(0, false);
  }, [open, kind, articleId, notificationId, loadPage]);

  const heading = title || HEADINGS[kind] || 'People';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: fullScreen ? '100%' : 360,
          maxWidth: '100%',
          bgcolor: tokens.surface,
          color: tokens.ink,
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
              {heading}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {total.toLocaleString()} {total === 1 ? 'person' : 'people'}
            </Typography>
          </Box>
          <Button onClick={onClose} size="small">
            Close
          </Button>
        </Stack>
        <Divider sx={{ mb: 1 }} />

        <ErrorAlert message={error} onClose={() => setError(null)} />

        {loading && <LoadingState label="Loading…" />}

        {!loading && (
          <List dense sx={{ flex: 1, overflow: 'auto', py: 0 }}>
            {items.map((p) => (
              <ListItem key={p.id} disableGutters sx={{ py: 1 }}>
                <ListItemText
                  primary={p.name}
                  secondary={p.role || p.email || undefined}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            ))}
            {items.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                No one yet.
              </Typography>
            )}
          </List>
        )}

        {nextOffset !== null && (
          <Button
            fullWidth
            variant="outlined"
            disabled={loadingMore}
            onClick={() => void loadPage(nextOffset, true)}
            sx={{ mt: 1 }}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        )}
      </Box>
    </Drawer>
  );
}
